import { ChangeDetectionStrategy, Component, computed, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AvatarsComponent, Person, TooltipModule } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { HealthHistoryDialogService } from '@libs/home';
import { CdkAccordionItem, CdkAccordionModule } from '@angular/cdk/accordion';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { selectIsEaglesoftPms, selectSiteSettingsPmsType } from '@libs/store';
import { Store } from '@ngrx/store';
import { Dialog } from '@angular/cdk/dialog';
import { FullNamePipe, AgePipe } from '../../pipes';
import { PatientInfoEditDialogComponent, PatientInfoEditDialogData } from '../patient-info-edit-dialog/patient-info-edit-dialog.component';
import { HealthHistoryPatientSecondaryContactsComponent } from './components/health-history-patient-details-secondary-contacts/health-history-patient-secondary-contacts.component';
import { HealthHistoryPatientDetailsItemComponent } from './components/health-history-patient-details-item/health-history-patient-details-item.component';
import { filterSecondaryContactsByPms } from './components/health-history-patient-details-secondary-contacts/secondary-contact-validators';

@Component({
  selector: 'lib-health-history-patient-details',
  standalone: true,
  imports: [
    CommonModule,
    FullNamePipe,
    AvatarsComponent,
    TranslateModule,
    TooltipModule,
    SvgIconComponent,
    AgePipe,
    CdkAccordionModule,
    HealthHistoryPatientSecondaryContactsComponent,
    HealthHistoryPatientDetailsItemComponent,
  ],
  templateUrl: './health-history-patient-details.component.html',
  styleUrl: './health-history-patient-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideToggle', [
      state('void', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1, overflow: 'hidden' })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
      transition('void => *', animate('300ms ease-in-out')),
    ]),
  ],
})
export class HealthHistoryPatientDetailsComponent {
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);
  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);

  private readonly accordionItem = viewChild<CdkAccordionItem>('accordionItem');

  protected readonly patient = computed(() => this.healthHistoryDialogService.viewModel().patient);
  protected readonly patientAvatar = computed(() => this.patient() as Person);
  protected readonly anyContactProvisional = computed(() => this.healthHistoryDialogService.viewModel().anyContactProvisional);
  protected readonly anyContactLockedByProvisional = computed(
    () => this.healthHistoryDialogService.viewModel().anyContactLockedByProvisional,
  );

  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  protected readonly isEaglesoftPms = this.store.selectSignal(selectIsEaglesoftPms);

  protected readonly secondaryContacts = computed(() =>
    filterSecondaryContactsByPms(this.healthHistoryDialogService.viewModel().secondaryContacts, this.pmsType()),
  );

  public closeAccordion(): void {
    const accordionItem = this.accordionItem();
    if (accordionItem && accordionItem.expanded) {
      accordionItem.close();
    }
  }

  protected onEditClick(event: Event): void {
    event.stopPropagation();
    const patient = this.patient();
    if (!patient) {
      return;
    }
    this.dialog.open<void, PatientInfoEditDialogData>(PatientInfoEditDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[480px]'],
      disableClose: false,
      data: {
        patient,
        secondaryContacts: this.secondaryContacts(),
      },
    });
  }
}
